// Copyright © 2026 Alex Bruce. All rights reserved.
import React, { useState, useEffect } from "react";
import type { Theme } from "../lib/theme";
import type { RFIItem, ShowToast } from "../types";
import type { Submittal } from "../lib/submittals";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
// Matches MAX_PER_BATCH on both endpoints. The client loops so no single
// request can outrun the serverless timeout, and an interrupted run is safe to
// restart -- the server ledger decides what still needs doing, not the client.
const BATCH = 10;

export type ExportKind = "rfi" | "submittal";

interface Option { id: number; name: string; email?: string }
interface LedgerRow {
  status: string; last_error: string | null;
  oe_rfi_id?: string; procore_rfi_id?: number | null; procore_rfi_number?: string | null;
  oe_submittal_id?: string; oe_submittal_number?: string | null;
  procore_submittal_id?: number | null; procore_submittal_number?: string | null;
}

interface Props {
  T: Theme;
  kind: ExportKind;
  rfis: RFIItem[];
  submittals: Submittal[];
  /** The cloud project id. Without one there is nothing to key the ledger to. */
  cloudProjectId: string | null;
  onClose: () => void;
  showToast: ShowToast;
}

/** Procore's submittal import template, in its exact column order. */
const CSV_HEADERS = [
  "Package Title", "Package Spec Section Number", "Package Spec Section Description",
  "Package Number", "Submittal Title", "Submittal Spec Section Number",
  "Submittal Spec Section Description", "Submittal Number", "Description",
  "Submittal Manager", "Submittal Status", "Submittal Type", "Location",
  "Received Date", "Issue Date", "Submit By Date", "Responsible Contractor Name",
  "Required On-Site Date", "Lead Time", "Design Team Review Time",
  "Internal Review Time", "Spec Section Area",
];

const SUBMITTAL_STATUS: Record<string, string> = {
  "not-started": "Draft", "info-requested": "Open", "in-review": "Open",
  "approved": "Approved", "rejected": "Revise and Resubmit", "not-required": "For Record Only",
};

const day = (iso?: string) => (iso ? String(iso).slice(0, 10) : "");
const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function ProcoreExport({ T, kind, rfis, submittals, cloudProjectId, onClose, showToast }: Props) {
  const isRfi = kind === "rfi";
  const [phase, setPhase] = useState<"loading" | "setup" | "running" | "done">("loading");
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState<Option[]>([]);
  const [projects, setProjects] = useState<Option[]>([]);
  const [users, setUsers] = useState<Option[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [managerId, setManagerId] = useState<number | null>(null);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [already, setAlready] = useState<Record<string, LedgerRow>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState<{ created: number; already: number; failed: number; errors: string[] }>(
    { created: 0, already: 0, failed: 0, errors: [] });

  // Void RFIs are working notes, not decisions; everything else goes, closed
  // ones especially, since the record is the point. Submittals have no void
  // state -- the whole log is the deliverable.
  const items: Array<RFIItem | Submittal> = isRfi
    ? rfis.filter(r => (r as RFIItem).status !== "void")
    : submittals;
  const pending = items.filter(i => already[i.id]?.status !== "done");
  const toSend = pending.filter(i => selected.has(i.id));

  // Procore needs a unique Submittal Number and, unlike RFIs, takes ours --
  // so OE-# goes in the number field itself. A submittal already filed keeps
  // the number the ledger recorded; new ones continue past the highest.
  const assignedNumbers = React.useMemo(() => {
    if (isRfi) return {};
    const used = Object.values(already)
      .map(r => Number(String(r.oe_submittal_number || "").replace(/^OE-/, "")))
      .filter(n => Number.isFinite(n) && n > 0);
    let next = (used.length ? Math.max(...used) : 0) + 1;
    const out: Record<string, string> = {};
    for (const s of [...submittals].sort((a, b) => (a.specSection || "").localeCompare(b.specSection || ""))) {
      const recorded = already[s.id]?.oe_submittal_number;
      out[s.id] = recorded || `OE-${next++}`;
    }
    return out;
  }, [isRfi, submittals, already]);

  const auth = async () => {
    const { supabase } = await import("../lib/supabase");
    const { data: { session } } = await supabase!.auth.getSession();
    if (!session?.access_token) throw new Error("Please sign in first");
    return { Authorization: `Bearer ${session.access_token}` };
  };

  const get = async (qs: string) => {
    const res = await fetch(`/api/procore-data?${qs}`, { headers: await auth() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Procore request failed");
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        if (!cloudProjectId) throw new Error(`Save this project to the cloud before exporting ${isRfi ? "RFIs" : "submittals"}.`);
        const status = await get("action=status");
        if (!status.connected) {
          const res = await fetch("/api/procore-auth-init", { method: "POST", headers: await auth() });
          if (!res.ok) throw new Error("Failed to start the Procore connection");
          const { url } = await res.json();
          window.location.href = url;
          return;
        }
        const [comp, hist] = await Promise.all([
          get("action=companies"),
          get(`action=${isRfi ? "rfi-status" : "submittal-status"}&oeProjectId=${encodeURIComponent(cloudProjectId)}`),
        ]);
        const rows: LedgerRow[] = hist.exports || [];
        const seen: Record<string, LedgerRow> = Object.fromEntries(
          rows.map(r => [String(isRfi ? r.oe_rfi_id : r.oe_submittal_id), r]));
        setAlready(seen);
        setCompanies(comp.companies || []);
        setSelected(new Set(items.filter(i => seen[i.id]?.status !== "done").map(i => i.id)));

        // A project already linked by anyone on this estimate wins, so RFIs
        // and submittals from one job cannot scatter across Procore projects.
        const link = hist.link;
        setCompanyId(link?.company_id ?? comp.companies?.[0]?.id ?? null);
        if (link?.procore_project_id) setProjectId(link.procore_project_id);
        setPhase("setup");
      } catch (e: any) {
        setError(e.message || "Could not reach Procore");
        setPhase("setup");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const d = await get(`action=projects&companyId=${companyId}`);
        const list: Option[] = (d.projects || []).filter((p: any) => p.active);
        setProjects(list);
        setProjectId(p => (p && list.some(x => x.id === p) ? p : list[0]?.id ?? null));
      } catch (e: any) { setError(e.message); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const d = await get(`action=users&projectId=${projectId}`);
        setUsers(d.users || []);
        const first = d.users?.[0]?.id ?? null;
        setManagerId(m => (m && d.users?.some((u: Option) => u.id === m) ? m : first));
        setAssigneeId(a => (a && d.users?.some((u: Option) => u.id === a) ? a : first));
      } catch (e: any) { setError(e.message); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const payloadFor = (i: any) => isRfi
    ? {
        id: i.id, number: i.number, subject: i.subject, status: i.status,
        priority: i.priority, question: i.question, response: i.response,
        submittedBy: i.submittedBy, assignedTo: i.assignedTo,
        createdAt: i.createdAt, answeredAt: i.answeredAt, dueDate: i.dueDate,
        drawingRef: i.drawingRef, specSection: i.specSection,
        costImpact: i.costImpact, scheduleImpact: i.scheduleImpact,
        linkedItems: Object.values(i.linkedItemSnapshots || {}),
      }
    : {
        id: i.id, number: assignedNumbers[i.id], specSection: i.specSection,
        costCode: i.costCode, description: i.description, status: i.status,
        requiredByDate: i.requiredByDate, responsibleParty: i.responsibleParty,
        notes: i.notes, isBestPracticeFlag: i.isBestPracticeFlag,
      };

  const run = async () => {
    if (!cloudProjectId || !companyId || !projectId || !managerId) return;
    if (isRfi && !assigneeId) return;
    setPhase("running");
    setError("");
    const chunks: any[][] = [];
    for (let i = 0; i < toSend.length; i += BATCH) chunks.push(toSend.slice(i, i + BATCH));
    setProgress({ done: 0, total: toSend.length });

    const tally = { created: 0, already: 0, failed: 0, errors: [] as string[] };
    const seen: Record<string, LedgerRow> = { ...already };

    for (const chunk of chunks) {
      try {
        const res = await fetch(isRfi ? "/api/procore-rfi-export" : "/api/procore-submittal-export", {
          method: "POST",
          headers: { ...(await auth()), "Content-Type": "application/json" },
          body: JSON.stringify({
            oeProjectId: cloudProjectId,
            companyId, procoreProjectId: projectId,
            procoreProjectName: projects.find(p => p.id === projectId)?.name || "",
            ...(isRfi
              ? { rfiManagerId: managerId, assigneeIds: [assigneeId], rfis: chunk.map(payloadFor) }
              : { submittalManagerId: managerId, submittals: chunk.map(payloadFor) }),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Export failed (${res.status})`);
        tally.created += data.created || 0;
        tally.already += data.already || 0;
        tally.failed += data.failed || 0;
        for (const r of data.results || []) {
          if (r.outcome === "failed") tally.errors.push(`${r.id}: ${r.detail || r.status}`);
          if (r.outcome === "created" || r.outcome === "already") {
            seen[r.id] = { status: "done", last_error: null,
              ...(isRfi
                ? { procore_rfi_number: r.procoreRfiNumber ?? null }
                : { oe_submittal_number: assignedNumbers[r.id] ?? null, procore_submittal_number: r.procoreNumber ?? null }) };
          }
        }
      } catch (e: any) {
        tally.failed += chunk.length;
        tally.errors.push(e.message || "Batch failed");
      }
      setProgress(p => ({ ...p, done: Math.min(p.done + chunk.length, p.total) }));
    }

    setAlready(seen);
    setSummary({ ...tally, errors: tally.errors.slice(0, 8) });
    setPhase("done");
    if (tally.failed === 0) showToast(`${tally.created} sent to Procore`);
  };

  /** Procore's own import template, for the cases the API cannot take. */
  const downloadCsv = () => {
    const mgr = users.find(u => u.id === managerId);
    const rows = toSend.map((raw) => {
      const s = raw as Submittal;
      const cell: Record<string, string> = {
        "Submittal Title": s.description || s.specSection || "Untitled",
        "Submittal Spec Section Number": s.specSection || "",
        "Submittal Number": assignedNumbers[s.id] || "",
        "Description": [s.notes, s.costCode ? `Cost code: ${s.costCode}` : ""].filter(Boolean).join(" — "),
        // The template wants an EMAIL that matches an active user in the
        // project directory, not a name and not an id.
        "Submittal Manager": mgr?.email || "",
        "Submittal Status": SUBMITTAL_STATUS[s.status] || "Open",
        "Responsible Contractor Name": s.responsibleParty || "",
        "Required On-Site Date": day(s.requiredByDate),
      };
      return CSV_HEADERS.map(h => csvCell(cell[h] ?? "")).join(",");
    });
    const blob = new Blob([[CSV_HEADERS.join(","), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `submittals-for-procore-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast(`${toSend.length} submittal${toSend.length === 1 ? "" : "s"} written to CSV`);
  };

  const label: React.CSSProperties = { fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 5, display: "block" };
  const field: React.CSSProperties = { width: "100%", boxSizing: "border-box", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textPrimary, fontSize: 12, fontFamily: FONT, padding: "7px 9px", borderRadius: 3, marginBottom: 14, outline: "none" };
  const btn = (primary: boolean): React.CSSProperties => ({
    background: primary ? T.bgAccentSubtle : "none",
    border: `1px solid ${primary ? T.borderAccent + "55" : T.borderSecondary}`,
    color: primary ? T.borderAccent : T.textMuted,
    cursor: "pointer", padding: "7px 16px", borderRadius: 3, fontSize: 11,
    fontWeight: primary ? 700 : 400, fontFamily: FONT,
  });

  const viewOf = (i: any) => isRfi
    ? { badge: `OE-${i.number}`, title: i.subject || "Untitled", meta: i.status }
    : { badge: assignedNumbers[i.id] || "OE-?", title: i.description || "Untitled", meta: i.specSection || "no spec section" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: T.bgModalBackdrop, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, width: "100%", maxWidth: 560, maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: FONT }}>

        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary }}>
              Send {isRfi ? "RFIs" : "Submittals"} to Procore
            </div>
            <div style={{ fontSize: 11, color: T.textWeak, marginTop: 3, lineHeight: 1.5 }}>
              {isRfi
                ? <>Each is created with an <strong style={{ color: T.borderAccent }}>OE-#</strong> subject prefix, carrying its original author, dates and linked cost items in the question body.</>
                : <>Each is filed under its <strong style={{ color: T.borderAccent }}>OE-#</strong> submittal number, with its cost code, responsible party and notes in the description.</>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontFamily: FONT, flexShrink: 0 }}><Icon.X size={12} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {error && (
            <div style={{ background: `${T.negative}14`, border: `1px solid ${T.negative}55`, borderRadius: 3, padding: "8px 10px", fontSize: 11, color: T.negative, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {phase === "loading" && <div style={{ fontSize: 12, color: T.textMuted, padding: "20px 0" }}>Checking your Procore connection…</div>}

          {phase === "setup" && (
            <>
              <label style={label}>Company</label>
              <select value={companyId ?? ""} onChange={e => setCompanyId(Number(e.target.value))} style={field}>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <label style={label}>Project</label>
              <select value={projectId ?? ""} onChange={e => setProjectId(Number(e.target.value))} style={field}>
                {projects.length === 0 && <option value="">No active projects</option>}
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              {/* Procore will not accept either object without a manager, and
                  knows nothing about OpenEstimate's free-text people. Chosen
                  once for the batch, not once per row. */}
              <label style={label}>{isRfi ? "RFI Manager" : "Submittal Manager"}</label>
              <select value={managerId ?? ""} onChange={e => setManagerId(Number(e.target.value))} style={field}>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ""}</option>)}
              </select>

              {isRfi && (<>
                <label style={label}>Assign to</label>
                <select value={assigneeId ?? ""} onChange={e => setAssigneeId(Number(e.target.value))} style={field}>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ""}</option>)}
                </select>
              </>)}

              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ ...label, marginBottom: 0 }}>Which {isRfi ? "RFIs" : "submittals"}</span>
                <span style={{ fontSize: 11 }}>
                  <button onClick={() => setSelected(new Set(pending.map(i => i.id)))}
                    style={{ background: "none", border: "none", color: T.borderAccent, cursor: "pointer", fontFamily: FONT, fontSize: 11, padding: "0 4px" }}>All</button>
                  <span style={{ color: T.borderSecondary }}>|</span>
                  <button onClick={() => setSelected(new Set())}
                    style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontFamily: FONT, fontSize: 11, padding: "0 4px" }}>None</button>
                </span>
              </div>
              <div style={{ border: `1px solid ${T.borderSecondary}`, borderRadius: 3, maxHeight: 190, overflowY: "auto", marginBottom: 14, background: T.bgInput }}>
                {items.length === 0 && (
                  <div style={{ padding: "14px 12px", fontSize: 11, color: T.textMuted }}>Nothing to send.</div>
                )}
                {items.map(raw => {
                  const i: any = raw;
                  const done = already[i.id]?.status === "done";
                  const v = viewOf(i);
                  const blocked = !isRfi && !i.specSection;
                  return (
                    <label key={i.id}
                      title={done ? "Already in Procore — re-sending would not create a duplicate"
                        : blocked ? "Procore requires a spec section number" : v.title}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                        borderBottom: `1px solid ${T.borderSecondary}44`,
                        cursor: done || blocked ? "default" : "pointer", opacity: done || blocked ? .55 : 1 }}>
                      <input type="checkbox" checked={selected.has(i.id) && !done && !blocked} disabled={done || blocked}
                        onChange={e => setSelected(prev => {
                          const n = new Set(prev);
                          e.target.checked ? n.add(i.id) : n.delete(i.id);
                          return n;
                        })}
                        style={{ flexShrink: 0, cursor: done || blocked ? "default" : "pointer" }} />
                      <span style={{ fontSize: 11, color: T.borderAccent, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{v.badge}</span>
                      <span style={{ fontSize: 11, color: T.textSecondary, flex: 1, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{v.title}</span>
                      <span style={{ fontSize: 10, color: blocked ? T.negative : T.textWeak, flexShrink: 0 }}>
                        {done ? "in Procore" : blocked ? "no spec section" : v.meta}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "10px 12px", fontSize: 11, color: T.textTertiary, lineHeight: 1.7 }}>
                <div><strong style={{ color: T.textSecondary }}>{toSend.length}</strong> selected to send</div>
                {items.length - pending.length > 0 && <div>{items.length - pending.length} already in Procore</div>}
                {isRfi && rfis.length - items.length > 0 && <div>{rfis.length - items.length} void — never sent</div>}
                <div style={{ color: T.textWeak, marginTop: 4 }}>
                  Procore records you as the creator. {isRfi ? "The original author stays in the question body." : "The OpenEstimate log stays in the description."}
                </div>
              </div>
            </>
          )}

          {phase === "running" && (
            <div style={{ padding: "16px 0" }}>
              <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Sending {progress.done} of {progress.total}…</div>
              <div style={{ height: 6, background: `${T.borderPrimary}55`, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: T.borderAccent, transition: "width .2s" }} />
              </div>
              <div style={{ fontSize: 11, color: T.textWeak, marginTop: 10 }}>
                Safe to leave — anything already sent is recorded and will not be sent twice.
              </div>
            </div>
          )}

          {phase === "done" && (
            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.9 }}>
              <div><strong style={{ color: T.positive }}>{summary.created}</strong> created in Procore</div>
              {summary.already > 0 && <div>{summary.already} were already there</div>}
              {summary.failed > 0 && <div style={{ color: T.negative }}>{summary.failed} failed</div>}
              {summary.errors.length > 0 && (
                <div style={{ marginTop: 10, background: `${T.negative}10`, border: `1px solid ${T.negative}44`, borderRadius: 3, padding: "8px 10px", fontSize: 11, color: T.textTertiary }}>
                  {summary.errors.map((e, i) => <div key={i} style={{ marginBottom: 3 }}>{e}</div>)}
                  <div style={{ color: T.textWeak, marginTop: 6 }}>Run it again to retry only the failures.</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderSecondary}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={btn(false)}>{phase === "done" ? "Close" : "Cancel"}</button>
          {/* Procore does have a CSV importer for submittals. Direct is the
              default; the file is the way out when the API refuses. */}
          {phase === "setup" && !isRfi && (
            <button onClick={downloadCsv} disabled={toSend.length === 0} style={{ ...btn(false), opacity: toSend.length === 0 ? .5 : 1 }}>
              <Icon.Download size={11} /> CSV instead
            </button>
          )}
          {phase === "setup" && (
            <button onClick={run} disabled={!projectId || !managerId || (isRfi && !assigneeId) || toSend.length === 0}
              style={{ ...btn(true), opacity: toSend.length === 0 ? .5 : 1, cursor: toSend.length === 0 ? "default" : "pointer" }}>
              Send {toSend.length}
            </button>
          )}
          {phase === "done" && summary.failed > 0 && (
            <button onClick={() => setPhase("setup")} style={btn(true)}>Retry failures</button>
          )}
        </div>
      </div>
    </div>
  );
}
